import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import poster from "@/assets/performance/hero-poster-848.webp.asset.json";
import videoAsset from "@/assets/performance/hero-background-v1.mp4.asset.json";
import webmAsset from "@/assets/performance/hero-background-v1.webm.asset.json";
import { shouldAutoplayBackground } from "@/lib/heroPlayback";

const HONEYBOOK_URL = "https://app.hellobonsai.com/s/n-santiago-media/strategycall";

const HeroSection = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const requested = useRef(false);
  const autoplayAllowed = useRef(false);

  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 767px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = (navigator as Navigator & { connection?: EventTarget & { saveData?: boolean } }).connection;
    let timer: number | undefined;
    let cancelled = false;
    const update = () => {
      window.clearTimeout(timer);
      autoplayAllowed.current = shouldAutoplayBackground({ mobile: mobile.matches, reducedMotion: reduced.matches, saveData: !!connection?.saveData });
      requested.current = false;
      setShowVideo(false);
      setPlaying(false);
      if (autoplayAllowed.current) {
        // Wait for critical content, the poster, and two painted frames.
        timer = window.setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!cancelled && autoplayAllowed.current) {
            requested.current = true;
            setShowVideo(true);
          }
        })), 1500);
      }
    };
    const posterImage = new Image();
    posterImage.src = poster.url;
    void posterImage.decode().catch(() => undefined).then(() => { if (!cancelled) update(); });
    mobile.addEventListener("change", update);
    reduced.addEventListener("change", update);
    connection?.addEventListener("change", update);
    const section = sectionRef.current;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.05 });
    if (section) observer.observe(section);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      observer.disconnect();
      mobile.removeEventListener("change", update);
      reduced.removeEventListener("change", update);
      connection?.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const sync = () => {
      if (visible && !document.hidden && requested.current) {
        void video.play().catch(() => setPlaying(false));
      } else video.pause();
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, [showVideo, visible]);

  const toggleVideo = () => {
    if (playing) {
      requested.current = false;
      videoRef.current?.pause();
    } else {
      requested.current = true;
      setShowVideo(true);
      void videoRef.current?.play().catch(() => setPlaying(false));
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center section-padding overflow-hidden bg-cover bg-center"
    >
      <img src={poster.url} alt="" aria-hidden="true" width={848} height={480} fetchPriority="high" loading="eager" className="absolute inset-0 w-full h-full object-cover" />
      {showVideo && visible && (
        <video
          ref={videoRef}
          src={document.createElement("video").canPlayType('video/mp4; codecs="avc1.64001f"') ? videoAsset.url : webmAsset.url}
          muted
          loop
          playsInline
          preload="none"
          poster={poster.url}
          onPlaying={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={() => { setShowVideo(false); setPlaying(false); }}
          className="absolute inset-0 w-full h-full object-cover"
          aria-label="Cinematic background video showcasing N. Santiago Media's dental marketing work for clinics across Florida"
        />
      )}

      <div className="absolute inset-0 bg-background/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background/90" />

      <div className="relative z-10 max-w-5xl mx-auto w-full">
        <div>
          <p className="text-primary font-semibold tracking-[0.25em] uppercase text-sm mb-6" style={{ fontFamily: "'Lora', serif" }}>
            N. Santiago Media
          </p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
            Dental marketing that turns visibility into <span className="gold-gradient-text">trust</span>, and trust into scheduled patients.
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mb-10 leading-relaxed">
            N. Santiago Media helps dental clinics get found, build patient trust, and convert more interest into scheduled appointments through marketing, content, reputation, and practice-level systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <Button variant="hero" size="lg" className="px-10 py-6 text-sm" asChild>
              <a href={HONEYBOOK_URL} target="_blank" rel="noopener noreferrer">
                Get My Dental Growth Audit
              </a>
            </Button>
            <Button variant="cta" size="lg" className="px-6 sm:px-10 py-6 text-sm whitespace-normal h-auto min-h-12 text-center" asChild>
              <a href="#growth-check">
                See Where Your Clinic Is Losing Patients
              </a>
            </Button>
          </div>
        </div>
      </div>
      <Button variant="ghost" size="sm" className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-muted-foreground max-w-[calc(100%-11rem)] h-auto whitespace-normal text-center" onClick={toggleVideo} aria-label={playing ? "Pause background video" : "Play background video"} aria-pressed={playing}>
        {playing ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
        {playing ? "Pause background video" : "Play background video"}
      </Button>
    </section>
  );
};

export default HeroSection;
